import React from 'react';
import {
  Box, Paper, TextField, Button, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TableSortLabel,
  Divider, CircularProgress, Stack, ToggleButtonGroup, ToggleButton, Grid,
  Collapse, TablePagination, Chip, Typography, Tooltip, Popover, List, ListItemButton, ListItemText
} from '@mui/material';
import { Download, GridView, Tab as TabIcon, Tune, Search } from '@mui/icons-material';

import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

// --------- helpers ---------
const pad2 = (n) => String(n).toString().padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0);
const sameMonth = (a,b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth();

const clampSameMonthAndOrder = (fromStr, toStr) => {
  if (!fromStr || !toStr) return { dateFrom: fromStr, dateTo: toStr, changed: false, note: '' };

  const from = new Date(fromStr);
  const to   = new Date(toStr);

  const monthMin = ymd(new Date(from.getFullYear(), from.getMonth(), 1));
  const monthMax = ymd(new Date(from.getFullYear(), from.getMonth() + 1, 0));

  let newTo = toStr;
  let changed = false;

  if (to < new Date(monthMin)) {
    newTo = monthMin;
    changed = true;
  }

  if (to > new Date(monthMax)) {
    newTo = monthMax;
    changed = true;
  }

  if (new Date(newTo) < from) {
    newTo = fromStr;
    changed = true;
  }

  return { dateFrom: fromStr, dateTo: newTo, changed, note: '' };
};

// formatting
const fmtQty = (n) => (n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const fmtMoney0 = (n) => (n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const fmtPct = (n) => (
  n == null || isNaN(n)
    ? '-'
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
);

const colorizePct = (pct, avg) => {
  if (pct == null || isNaN(Number(pct))) return '-';

  const hasAvg = typeof avg === 'number' && !isNaN(avg);
  const colorKey = hasAvg
    ? Number(pct) < avg
      ? 'error.main'
      : Number(pct) > avg
        ? 'success.main'
        : 'text.primary'
    : 'text.primary';

  return (
    <Box component="span" sx={{ color: colorKey }}>
      {fmtPct(pct)}
    </Box>
  );
};

const API_URL_PM_BY_LOCATION = BackofficeReportsApi.getMarginsByProductUrl();
const API_URL_PM_BY_PRODUCT_LOCATIONS = BackofficeReportsApi.getMarginsByProductLocationsUrl();

const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

// CSV
const toCSV = (rows) => rows.map(r => r.map(x => `"${String(x ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');

const downloadCSV = (filename, rows) => {
  const csv = "\uFEFF" + toCSV(rows);

  try {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.style.display = 'none';
    a.href = url;
    a.setAttribute('download', filename);

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch (e) {
    const uri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    window.open(uri, '_blank');
  }
};

// --------- Chip Picker ---------
function ChipPicker({ label, options, value, onChange, hint = '', dim = false, searchable = true }) {
  const [anchor, setAnchor] = React.useState(null);
  const [q, setQ] = React.useState('');

  const open = Boolean(anchor);
  const selected = options.find(o => o.value === value) || options[0];
  const filtered = options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()));

  const chip = (
    <Chip
      size="medium"
      variant="outlined"
      label={`${label}: ${selected?.label || ''}`}
      onClick={(e) => setAnchor(e.currentTarget)}
      sx={{
        opacity: dim ? 0.55 : 1,
        cursor: 'pointer',
        width: '100%',
        maxWidth: '100%',
        height: 34,
        borderRadius: '12px',
        justifyContent: 'flex-start',
        fontSize: {
          xs: '0.76rem',
          sm: '0.86rem',
        },
        '& .MuiChip-label': {
          px: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    />
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {hint ? <Tooltip title={hint}>{chip}</Tooltip> : chip}

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQ('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: {
              xs: 'calc(100vw - 32px)',
              sm: 360,
            },
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
      >
        {searchable && (
          <>
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search fontSize="small" />

              <TextField
                size="small"
                placeholder={`Search ${label.toLowerCase()}…`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.84rem',
                    },
                  },
                }}
              />

              <Button
                size="small"
                onClick={() => {
                  onChange(options[0].value);
                  setAnchor(null);
                  setQ('');
                }}
              >
                Clear
              </Button>
            </Box>

            <Divider />
          </>
        )}

        <List dense sx={{ overflowY: 'auto', maxHeight: 'min(52vh, 360px)' }}>
          {filtered.map(o => (
            <ListItemButton
              key={o.value}
              dense
              selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setAnchor(null);
                setQ('');
              }}
            >
              <ListItemText primaryTypographyProps={{ noWrap: true }} primary={o.label} />
            </ListItemButton>
          ))}

          {!filtered.length && (
            <ListItemButton disabled dense>
              <ListItemText primary="No matches" />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </Box>
  );
}

// --------- compact filters ---------
class SimpleFilters extends React.Component {
  state = {
    client: new Client('none'),
    open: true,
    dateFrom: ymd(startOfMonth(new Date())),
    dateTo: ymd(new Date()),
    productOpts: [{ value: '__ALL__', label: 'All Products' }],
    productSel: '__ALL__',
    locationOpts: [{ value: '__ALL__', label: 'All Locations' }],
    locationSel: '__ALL__',
    deliverySel: '',
    appClientsOnly: false,
    loading: true,
    adjustNote: '',
  };

  async componentDidMount() {
    try {
      const client = this.state.client;

      const [pRes, lRes] = await Promise.all([
        client.getData(API_URL_PRODUCTS, { isForSale: 1, orderBy_name: true }) || {},
        client.getData(API_URL_LOCATIONS, { status: 1 }) || {},
      ]);

      const products = pRes?.products || pRes?.results || pRes || [];
      const locations = lRes?.locations || lRes?.results || lRes || [];

      const productOpts = [
        { value: '__ALL__', label: 'All Products' },
        ...products.map(p => ({
          value: String(p.iref || p.id),
          label: p.name,
        })),
      ];

      const locationOpts = [
        { value: '__ALL__', label: 'All Locations' },
        ...locations
          .map(l => ({
            value: String(l.iref || l.id),
            label: l.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];

      this.setState({ productOpts, locationOpts, loading: false }, this.propagate);
    } catch {
      this.setState({ loading: false });
    }
  }

  setPreset = (k) => {
    const now = new Date();

    if (k === 'FULL') {
      this.setState({
        dateFrom: ymd(startOfMonth(now)),
        dateTo: ymd(endOfMonth(now)),
      }, this.propagate);
    }

    if (k === 'MTD') {
      this.setState({
        dateFrom: ymd(startOfMonth(now)),
        dateTo: ymd(now),
      }, this.propagate);
    }

    if (k === 'FIRST10') {
      this.setState({
        dateFrom: ymd(startOfMonth(now)),
        dateTo: ymd(new Date(now.getFullYear(), now.getMonth(), 10)),
      }, this.propagate);
    }
  };

  propagate = () => {
    const { dateFrom, dateTo, productSel, locationSel, productOpts, locationOpts } = this.state;

    const d1 = new Date(dateFrom);
    const d2 = new Date(dateTo);

    if (!sameMonth(d1, d2) || d1 > d2) return;

    const pIrefs = productSel === '__ALL__' ? [] : [productSel];
    const locIrefs = locationSel === '__ALL__' ? [] : [locationSel];

    const pLabel = productOpts.find(o => o.value === productSel)?.label || 'All Products';
    const lLabel = locationOpts.find(o => o.value === locationSel)?.label || 'All Locations';

    const deliveryLabel =
      this.state.deliverySel === '1'
        ? 'Delivery: Yes'
        : this.state.deliverySel === '0'
          ? 'Delivery: No'
          : 'Delivery: Any';
    const clientsLabel = this.state.appClientsOnly ? 'Clients: App Clients' : 'Clients: All';

    const summary = [`${dateFrom} → ${dateTo}`, pLabel, lLabel, deliveryLabel, clientsLabel].join(' · ');

    this._draft = {
      dateFrom,
      dateTo,
      productIrefs: pIrefs,
      locationIrefs: locIrefs,
      appClientsOnly: this.state.appClientsOnly,
      summaryLabel: summary,
    };
  };

  apply = () => {
    if (!this._draft) this.propagate();
    if (!this._draft) return;

    this.props.onChange?.({
      ...this._draft,
      delivery: this.state.deliverySel,
    });
  };

  render() {
    const {
      open,
      dateFrom,
      dateTo,
      productOpts,
      productSel,
      locationOpts,
      locationSel,
      loading,
    } = this.state;

    const { activeTab } = this.props;

    const productBackendInactive = activeTab === 0;
    const locationBackendInactive = activeTab === 1;

    const dateFieldSx = {
      minWidth: 0,
      '& .MuiInputBase-root': {
        height: 34,
        borderRadius: '12px',
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
        px: {
          xs: 0.75,
          sm: 1,
        },
      },
    };

    return (
      <Paper
        sx={{
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
          <Button
            size="small"
            startIcon={<Tune fontSize="small" />}
            onClick={() => this.setState({ open: !open })}
            sx={{
              height: 30,
              minWidth: 86,
              borderRadius: '11px',
              textTransform: 'none',
              fontWeight: 750,
              px: 1,
            }}
          >
            Filters
          </Button>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 650,
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.84rem' },
            }}
          >
            {this.props.summary || 'Choose filters and apply'}
          </Typography>
        </Stack>

        <Collapse in={open} timeout="auto">
          <Divider sx={{ my: 0.75 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: 'repeat(4, minmax(0, 1fr))',
                md: 'repeat(7, minmax(0, 1fr))',
              },
              gap: {
                xs: 0.65,
                sm: 0.8,
              },
              alignItems: 'center',
            }}
          >
            {loading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  gridColumn: '1 / -1',
                  px: 0.25,
                }}
              >
                Loading filters…
              </Typography>
            )}

            <TextField
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => {
                const nextFrom = e.target.value;
                const adj = clampSameMonthAndOrder(nextFrom, this.state.dateTo);

                this.setState(
                  {
                    dateFrom: adj.dateFrom,
                    dateTo: adj.dateTo,
                    adjustNote: adj.changed ? adj.note : '',
                  },
                  this.propagate
                );
              }}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => {
                const nextTo = e.target.value;
                const adj = clampSameMonthAndOrder(this.state.dateFrom, nextTo);

                this.setState(
                  {
                    dateFrom: adj.dateFrom,
                    dateTo: adj.dateTo,
                    adjustNote: adj.changed ? adj.note : '',
                  },
                  this.propagate
                );
              }}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />

            <Box sx={{ minWidth: 0 }}>
              <ChipPicker
                label="Product"
                options={productOpts}
                value={productSel}
                onChange={(v) => this.setState({ productSel: v }, this.propagate)}
                hint={productBackendInactive ? 'Local filter only in this tab' : ''}
                dim={productBackendInactive}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <ChipPicker
                label="Location"
                options={locationOpts}
                value={locationSel}
                onChange={(v) => this.setState({ locationSel: v }, this.propagate)}
                hint={locationBackendInactive ? 'Local filter only in this tab' : ''}
                dim={locationBackendInactive}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <ChipPicker
                label="Delivery"
                options={[
                  { value: '', label: 'Any' },
                  { value: '0', label: 'No' },
                  { value: '1', label: 'Yes' },
                ]}
                value={this.state.deliverySel}
                onChange={(v) => this.setState({ deliverySel: v }, this.propagate)}
                searchable={false}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <ChipPicker
                label="Clients"
                options={[
                  { value: false, label: 'All' },
                  { value: true, label: 'App Clients' },
                ]}
                value={this.state.appClientsOnly}
                onChange={(v) => this.setState({ appClientsOnly: v }, this.propagate)}
                searchable={false}
              />
            </Box>

            <Button
              size="small"
              variant="contained"
              startIcon={<Search />}
              onClick={this.apply}
              sx={{
                height: 34,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 750,
                gridColumn: {
                  xs: '1 / -1',
                  sm: 'auto',
                },
                justifySelf: {
                  xs: 'stretch',
                  sm: 'end',
                },
              }}
            >
              Apply
            </Button>

            {this.state.adjustNote && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{
                  gridColumn: '1 / -1',
                  px: 0.25,
                }}
              >
                {this.state.adjustNote}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  }
}

// --------- generic compact table ---------
function CompactTable({
  rows,
  totals,
  loading,
  onExport,
  firstColTitle = 'Location',
  firstColField = 'locationName',
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRpp] = React.useState(-1);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(null);

  const handleRequestSort = (property) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const sortedRows = orderBy
    ? [...rows].sort((a, b) => {
        const getValue = (row) => orderBy === firstColField
          ? String(row[firstColField] || '').toLocaleLowerCase()
          : Number(row[orderBy] || 0);
        const aValue = getValue(a);
        const bValue = getValue(b);
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      })
    : rows;

  const pageRows = rowsPerPage > 0
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;

  const sortLabel = (property, label) => (
    <TableSortLabel
      active={orderBy === property}
      direction={orderBy === property ? order : 'asc'}
      onClick={() => handleRequestSort(property)}
    >
      {label}
    </TableSortLabel>
  );

  const bodyCellSx = {
    py: { xs: 0.45, sm: 0.65, md: 0.8 },
    px: { xs: 0.35, sm: 0.65, md: 1 },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#111827',
    fontSize: {
      xs: '0.68rem',
      sm: '0.82rem',
      md: '0.92rem',
    },
    lineHeight: 1.15,
    fontVariantNumeric: 'tabular-nums',
  };

  const headerCellSx = {
    py: { xs: 0.45, sm: 0.65, md: 0.85 },
    px: { xs: 0.35, sm: 0.65, md: 1 },
    bgcolor: '#f8fafc',
    color: '#1a237e',
    fontWeight: 700,
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: {
      xs: '0.68rem',
      sm: '0.82rem',
      md: '0.92rem',
    },
  };

  const metricChipSx = {
    height: { xs: 22, sm: 26 },
    borderRadius: '9px',
    fontWeight: 650,
    fontSize: { xs: '0.66rem', sm: '0.78rem' },
    '& .MuiChip-label': {
      px: { xs: 0.55, sm: 0.8 },
    },
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: '14px',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 0.75,
          p: {
            xs: 0.5,
            sm: 0.85,
          },
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.4,
            alignItems: 'center',
            flexWrap: 'wrap',
            overflow: 'hidden',
            minWidth: 0,
            flex: 1,
          }}
        >
          {totals && (
            <>
              <Chip size="small" label={<><b>Qty:</b>&nbsp;{fmtQty(totals.qty)}</>} sx={metricChipSx} />
              <Chip size="small" label={<><b>Value:</b>&nbsp;{fmtMoney0(totals.gross)}</>} sx={metricChipSx} />
              <Chip size="small" label={<><b>Margin:</b>&nbsp;{fmtMoney0(totals.margin)}</>} sx={metricChipSx} />
              <Chip size="small" label={<><b>%:</b>&nbsp;{fmtPct(totals.marginPct)}</>} sx={metricChipSx} />
            </>
          )}
        </Box>

        <Tooltip title="Download CSV">
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!rows.length}
              onClick={onExport}
              sx={{
                minWidth: { xs: 34, sm: 70 },
                width: { xs: 34, sm: 'auto' },
                height: { xs: 30, sm: 32 },
                borderRadius: '11px',
                textTransform: 'none',
                fontWeight: 750,
                flexShrink: 0,
                px: { xs: 0, sm: 1 },
              }}
            >
              <Download fontSize="small" />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                CSV
              </Box>
            </Button>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
            width: '100%',
            '& .MuiTableRow-hover:hover': {
              backgroundColor: 'rgba(26, 35, 126, 0.035)',
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              width: '100%',
              minWidth: 0,
              tableLayout: 'fixed',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 6,
                    bgcolor: '#f8fafc',
                    backgroundClip: 'padding-box',
                    width: {
                      xs: '44%',
                      sm: '46%',
                      md: '42%',
                    },
                    minWidth: 0,
                    boxShadow: {
                      xs: '8px 0 10px -10px rgba(15, 23, 42, 0.35)',
                      md: 'none',
                    },
                  }}
                >
                  {sortLabel(firstColField, firstColTitle)}
                </TableCell>

                {[
                  ['Qty', 'qty', { xs: '13%', sm: '13%', md: '14%' }],
                  ['Value', 'gross', { xs: '16%', sm: '15%', md: '16%' }],
                  ['Margin', 'margin', { xs: '16%', sm: '15%', md: '16%' }],
                  ['%', 'marginPct', { xs: '11%', sm: '11%', md: '12%' }],
                ].map(([label, property, width]) => (
                  <TableCell
                    key={label}
                    align="right"
                    sx={{
                      ...headerCellSx,
                      width,
                    }}
                  >
                    {sortLabel(property, label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: '#fff',
                      backgroundClip: 'padding-box',
                      fontWeight: 650,
                      width: {
                        xs: '44%',
                        sm: '46%',
                        md: '42%',
                      },
                      minWidth: 0,
                      boxShadow: {
                        xs: '8px 0 10px -10px rgba(15, 23, 42, 0.35)',
                        md: 'none',
                      },
                    }}
                    title={r[firstColField]}
                  >
                    {r[firstColField]}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {fmtQty(r.qty)}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {fmtMoney0(r.gross)}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {fmtMoney0(r.margin)}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {colorizePct(r.marginPct, totals?.marginPct)}
                  </TableCell>
                </TableRow>
              ))}

              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={bodyCellSx}>
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRpp(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
        sx={{
          flexShrink: 0,
          borderTop: '1px solid rgba(15, 23, 42, 0.06)',
          minHeight: { xs: 38, sm: 50 },
          overflow: 'hidden',
          '& .MuiTablePagination-toolbar': {
            minHeight: { xs: 38, sm: 50 },
            px: { xs: 0.25, sm: 1 },
            flexWrap: 'nowrap',
            gap: { xs: 0.25, sm: 0.5 },
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.72rem', sm: '0.86rem' },
            m: 0,
          },
          '& .MuiTablePagination-actions': {
            ml: { xs: 0.25, sm: 1 },
          },
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.72rem', sm: '0.86rem' },
            mr: { xs: 0.25, sm: 1 },
          },
          '& .MuiIconButton-root': {
            p: { xs: 0.35, sm: 0.5 },
          },
        }}
      />
    </Paper>
  );
}

// --------- Panels ---------
// Tab 1: By Product
class PanelProducts extends React.Component {
  state = {
    client: new Client('none'),
    loading: false,
    rows: [],
    totals: null,
    lastFilters: null,
  };

  async fetch(f) {
    if (!f) return this.setState({ rows: [], totals: null, lastFilters: null });

    const { dateFrom, dateTo, productIrefs, locationIrefs, delivery, appClientsOnly } = f;
    const params = { dateFrom, dateTo };

    if (locationIrefs?.length === 1) params.location = locationIrefs[0];
    if (delivery === '0' || delivery === '1') params.delivery = delivery;
    if (appClientsOnly) params.appClientsOnly = 1;

    this.setState({ loading: true, lastFilters: f });

    try {
      const data = await this.state.client.getData(API_URL_PM_BY_LOCATION, params) || {};
      const raw = data.rows || [];

      let filteredRaw = raw;

      const zeroRaw = Array.isArray(filteredRaw)
        ? filteredRaw.filter(r => Number(r.unitCoga) === 0 || Number(r.cogs) === 0 || Number(r.marginPct) === 100)
        : [];

      const zeroNames = zeroRaw.map(r => `${r.productName || r.product__name || 'Product'}`);

      if (locationIrefs?.length > 1) {
        const set = new Set(locationIrefs.map(String));
        filteredRaw = raw.filter(r => set.has(String(r.locationIref)));
      }

      const map = new Map();

      for (const r of filteredRaw) {
        const pid = String(r.productIref ?? r.product__iref ?? r.product_id);
        const pname = r.productName ?? r.product__name ?? 'Product';

        const cur = map.get(pid) || {
          productIref: pid,
          productName: pname,
          qty: 0,
          gross: 0,
          net: 0,
          margin: 0,
        };

        cur.qty += Number(r.qty || 0);
        cur.gross += Number(r.gross || 0);

        const net = Number(r.net || (Number(r.gross || 0) - Number(r.vat || 0)));

        cur.net += net;
        cur.margin += Number(r.margin || (net - Number(r.cogs || 0)));

        map.set(pid, cur);
      }

      let rows = Array.from(map.values()).map(x => ({
        productIref: x.productIref,
        productName: x.productName,
        qty: x.qty,
        gross: x.gross,
        margin: x.margin,
        marginPct: x.net ? (x.margin / x.net * 100.0) : null,
      }));

      if (productIrefs?.length) {
        const set = new Set(productIrefs.map(String));
        rows = rows.filter(r => set.has(String(r.productIref)));
      }

      const t = rows.reduce((a, r) => {
        a.qty += r.qty;
        a.gross += r.gross;
        a.margin += r.margin;
        return a;
      }, {
        qty: 0,
        gross: 0,
        margin: 0,
      });

      const keep = new Set(rows.map(r => String(r.productIref)));

      const totalNet = filteredRaw
        .filter(r => keep.has(String(r.productIref || r.product__iref || r.product_id)))
        .reduce((s, r) => s + Number(r.net || (Number(r.gross || 0) - Number(r.vat || 0))), 0);

      const totals = {
        qty: t.qty,
        gross: t.gross,
        margin: t.margin,
        marginPct: totalNet ? (t.margin / totalNet * 100.0) : null,
      };

      const z5 = zeroNames.slice(0, 5);
      const zMore = Math.max(0, zeroNames.length - 5);

      this.props.onWarn?.(z5, zMore);
      this.setState({ rows, totals, loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  }

  exportCSV = () => {
    const { rows, totals, lastFilters } = this.state;

    const productInfo = lastFilters?.productIrefs?.length
      ? `${lastFilters.productIrefs.length} selected`
      : 'All Products';

    const locationInfo = lastFilters?.locationIrefs?.length
      ? `${lastFilters.locationIrefs.length} selected (client-side)`
      : 'All Locations';

    const meta = [
      ['Report', 'By Product — Margin across Locations'],
      ['Period', `${lastFilters?.dateFrom || ''} to ${lastFilters?.dateTo || ''}`],
      ['Products', productInfo],
      ['Locations', locationInfo],
      ['Clients', lastFilters?.appClientsOnly ? 'App Clients' : 'All'],
      ['Generated At', new Date().toISOString()],
    ];

    const header = ['Product', 'Qty', 'Value', 'Margin', 'Margin%'];
    const label = lastFilters ? `${lastFilters.dateFrom}_to_${lastFilters.dateTo}` : 'period';

    const out = [
      ...meta,
      [],
      header,
      ...rows.map(r => [
        r.productName,
        fmtQty(r.qty),
        fmtMoney0(r.gross),
        fmtMoney0(r.margin),
        fmtPct(r.marginPct),
      ]),
    ];

    if (totals) {
      out.push([]);
      out.push([
        'TOTALS',
        fmtQty(totals.qty),
        fmtMoney0(totals.gross),
        fmtMoney0(totals.margin),
        fmtPct(totals.marginPct),
      ]);
    }

    downloadCSV(`by_product_${label}.csv`, out);
  };

  render() {
    const { rows, totals, loading } = this.state;

    return (
      <CompactTable
        rows={rows}
        totals={totals}
        loading={loading}
        onExport={this.exportCSV}
        firstColTitle="Product"
        firstColField="productName"
      />
    );
  }
}

// Tab 2: By Location
class PanelLocations extends React.Component {
  state = {
    client: new Client('none'),
    loading: false,
    rows: [],
    totals: null,
    lastFilters: null,
  };

  async fetch(f) {
    if (!f) return this.setState({ rows: [], totals: null, lastFilters: null });

    const { dateFrom, dateTo, productIrefs, locationIrefs, delivery, appClientsOnly } = f;
    const params = { dateFrom, dateTo };

    if (productIrefs?.length) params.product = productIrefs.join(',');
    if (delivery === '0' || delivery === '1') params.delivery = delivery;
    if (appClientsOnly) params.appClientsOnly = 1;

    this.setState({ loading: true, lastFilters: f });

    try {
      const data = await this.state.client.getData(API_URL_PM_BY_PRODUCT_LOCATIONS, params) || {};
      let rows = data.rows || [];

      if (locationIrefs?.length) {
        const set = new Set(locationIrefs.map(String));
        rows = rows.filter(r => set.has(String(r.locationIref)));
      }

      const zeroRows = Array.isArray(rows)
        ? rows.filter(r => Number(r.unitCoga) === 0 || Number(r.cogs) === 0 || Number(r.marginPct) === 100)
        : [];

      const names = zeroRows.map(r => `${r.productName || 'Product'}`);

      const z5 = names.slice(0, 5);
      const zMore = Math.max(0, names.length - 5);

      this.props.onWarn?.(z5, zMore);
      this.setState({ rows, totals: data.totals || null, loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  }

  exportCSV = () => {
    const { rows, totals, lastFilters } = this.state;

    const productInfo = lastFilters?.productIrefs?.length
      ? `${lastFilters.productIrefs.length} selected`
      : 'All Products';

    const locationInfo = lastFilters?.locationIrefs?.length
      ? `${lastFilters.locationIrefs.length} selected (client-side)`
      : 'All Locations';

    const meta = [
      ['Report', 'By Location — Margin across Products'],
      ['Period', `${lastFilters?.dateFrom || ''} to ${lastFilters?.dateTo || ''}`],
      ['Products', productInfo],
      ['Locations', locationInfo],
      ['Clients', lastFilters?.appClientsOnly ? 'App Clients' : 'All'],
      ['Generated At', new Date().toISOString()],
    ];

    const header = ['Location', 'Qty', 'Value', 'Margin', 'Margin%'];
    const label = lastFilters ? `${lastFilters.dateFrom}_to_${lastFilters.dateTo}` : 'period';

    const out = [
      ...meta,
      [],
      header,
      ...rows.map(r => [
        r.locationName,
        fmtQty(r.qty),
        fmtMoney0(r.gross),
        fmtMoney0(r.margin),
        fmtPct(r.marginPct),
      ]),
    ];

    if (totals) {
      out.push([]);
      out.push([
        'TOTALS',
        fmtQty(totals.qty),
        fmtMoney0(totals.gross),
        fmtMoney0(totals.margin),
        fmtPct(totals.marginPct),
      ]);
    }

    downloadCSV(`by_location_${label}.csv`, out);
  };

  render() {
    const { rows, totals, loading } = this.state;

    return (
      <CompactTable
        rows={rows}
        totals={totals}
        loading={loading}
        onExport={this.exportCSV}
        firstColTitle="Location"
        firstColField="locationName"
      />
    );
  }
}

// --------- Main page with Tabs or Grid ---------
export default class ProductMarginTabs_ChipSelect_Tight extends React.Component {
  constructor(props) {
    super(props);

    const savedLayout = typeof window !== 'undefined'
      ? localStorage.getItem('pm_layout')
      : null;

    const isDesktop = typeof window !== 'undefined'
      ? window.innerWidth >= 1200
      : false;

    this.state = {
      tab: 0,
      filters: null,
      layout: savedLayout || 'tabs',
      summary: '',
      cogaZeroProds: [],
      cogaZeroProdsMore: 0,
      isDesktop,
    };
  }

  componentDidMount() {
    if (typeof window === 'undefined') return;

    this._resizeHandler = () => {
      const isDesktop = window.innerWidth >= 1200;

      if (isDesktop !== this.state.isDesktop) {
        this.setState({ isDesktop }, () => {
          if (!this.state.filters) return;

          if (isDesktop) {
            this.tab0?.fetch?.(this.state.filters);
            this.tab1?.fetch?.(this.state.filters);
          } else if (this.state.layout === 'tabs') {
            const ref = this.state.tab === 0 ? this.tab0 : this.tab1;
            ref?.fetch?.(this.state.filters);
          } else {
            this.tab0?.fetch?.(this.state.filters);
            this.tab1?.fetch?.(this.state.filters);
          }
        });
      }
    };

    window.addEventListener('resize', this._resizeHandler);
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined' && this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  fetchForCurrentView = (filters = this.state.filters) => {
    if (!filters) return;

    if (this.state.isDesktop || this.state.layout === 'grid') {
      this.tab0?.fetch?.(filters);
      this.tab1?.fetch?.(filters);
      return;
    }

    const ref = this.state.tab === 0 ? this.tab0 : this.tab1;
    ref?.fetch?.(filters);
  };

  renderHeaderBar() {
    const { tab, layout } = this.state;

    return (
      <Paper
        elevation={0}
        sx={{
          mb: 0.5,
          px: { xs: 0.5, sm: 1 },
          py: 0.4,
          borderRadius: '12px',
          border: '1px solid rgba(15, 23, 42, 0.06)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            minWidth: 0,
            width: '100%',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => {
              this.setState({ tab: v }, () => {
                if (!this.state.isDesktop && this.state.layout === 'tabs') {
                  const ref = v === 0 ? this.tab0 : this.tab1;
                  ref?.fetch?.(this.state.filters);
                }
              });
            }}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 34,
              flex: 1,
              minWidth: 0,
              '& .MuiTabs-indicator': {
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: 34,
                px: { xs: 1, sm: 1.25 },
                py: 0,
                fontSize: { xs: '0.78rem', sm: '0.86rem' },
                textTransform: 'none',
                fontWeight: 750,
              },
            }}
          >
            <Tab label="By Product" />
            <Tab label="By Location" />
          </Tabs>

          <ToggleButtonGroup
            size="small"
            value={layout}
            exclusive
            onChange={(_e, v) => {
              if (!v) return;

              this.setState({ layout: v }, () => this.fetchForCurrentView());
              localStorage.setItem('pm_layout', v);
            }}
            sx={{
              flexShrink: 0,
              display: {
                xs: 'inline-flex',
                lg: 'none',
              },
              '& .MuiToggleButton-root': {
                width: 32,
                height: 30,
                p: 0,
                borderRadius: '10px',
              },
            }}
          >
            <ToggleButton value="tabs">
              <TabIcon fontSize="small" />
            </ToggleButton>

            <ToggleButton value="grid">
              <GridView fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
    );
  }

  renderMobileContent() {
    const { tab, layout } = this.state;

    if (layout === 'grid') {
      return (
        <Grid
          container
          spacing={0.75}
          sx={{
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              height: '50%',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
            }}
          >
            <PanelProducts
              ref={(r) => { this.tab0 = r; }}
              onWarn={(prods, more) =>
                this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              height: '50%',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
            }}
          >
            <PanelLocations
              ref={(r) => { this.tab1 = r; }}
              onWarn={(prods, more) =>
                this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
              }
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {tab === 0 ? (
          <PanelProducts
            ref={(r) => { this.tab0 = r; }}
            onWarn={(prods, more) =>
              this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
            }
          />
        ) : (
          <PanelLocations
            ref={(r) => { this.tab1 = r; }}
            onWarn={(prods, more) =>
              this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
            }
          />
        )}
      </Box>
    );
  }

  renderDesktopContent() {
    return (
      <Grid
        container
        spacing={1}
        sx={{
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          lg={6}
          sx={{
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <PanelProducts
            ref={(r) => { this.tab0 = r; }}
            onWarn={(prods, more) =>
              this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
            }
          />
        </Grid>

        <Grid
          item
          lg={6}
          sx={{
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <PanelLocations
            ref={(r) => { this.tab1 = r; }}
            onWarn={(prods, more) =>
              this.setState({ cogaZeroProds: prods, cogaZeroProdsMore: more })
            }
          />
        </Grid>
      </Grid>
    );
  }

  render() {
    const { tab, summary, isDesktop } = this.state;

    return (
      <Box
        sx={{
          width: '100%',
          height: {
            xs: '100%',
            sm: '100%',
            md: '100%',
            lg: '100%',
          },
          minHeight: 0,
          maxHeight: '100%',
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
        }}
      >
        {this.renderHeaderBar()}

        <SimpleFilters
          activeTab={tab}
          summary={summary}
          onChange={(f) => {
            this.setState({ filters: f, summary: f?.summaryLabel || '' }, () => {
              this.fetchForCurrentView(f);
            });
          }}
        />

        {Array.isArray(this.state.cogaZeroProds) && this.state.cogaZeroProds.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              px: 0.75,
              textAlign: 'left',
              mb: 0.75,
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
              }}
            >
              Products with cost 0: {this.state.cogaZeroProds.join(', ')}
              {this.state.cogaZeroProdsMore ? `, …${this.state.cogaZeroProdsMore} products more` : ''}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {isDesktop ? this.renderDesktopContent() : this.renderMobileContent()}
        </Box>
      </Box>
    );
  }
}
